import React, { useCallback } from 'react';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import Brightness3Icon from '@mui/icons-material/Brightness3';
import WbTwilightIcon from '@mui/icons-material/WbTwilight';
import NightlightIcon from '@mui/icons-material/Nightlight';
import {
  PictureAsPdf,
  VideoFile,
  Quiz,
  Help,
  Html,
  DataObject,
  Description,
  TableChart,
  MenuBook,
  VideoLibrary,
  Computer,
  Assignment,
  School,
  Science
} from '@mui/icons-material';
import { AnnouncementItem } from '../components/shared/AnnouncementWidget/AnnouncementAlertItem';

export interface TimeGreeting {
  greeting: string;
  icon: React.ReactElement;
  gradient: string;
  bgColor: string;
}

export const motivationalQuotes: string[] = [
  'Dream big, work hard, stay focused!',
  'Success is a journey, not a destination.',
  'Every expert was once a beginner.',
  'Progress, not perfection.',
  'Your only limit is your mind.',
  'Great things never come from comfort zones.',
  'Learning never exhausts the mind.',
  "Believe you can and you're halfway there.",
  'The best time to plant a tree was 20 years ago. The second best time is now.',
  'Knowledge is power, but action is key.',
  'Small steps daily lead to big changes yearly.',
  "Excellence is not a skill, it's an attitude.",
  'Focus on being productive instead of busy.',
  'Success starts with self-discipline.',
  'Turn your wounds into wisdom.',
  'The future belongs to those who learn.',
  'Consistency beats perfection every time.',
  'Your potential is endless.',
  'Make today amazing!',
  "Keep going, you're doing great!"
];

export const getRandomQuote = (): string => {
  const randomIndex = Math.floor(Math.random() * motivationalQuotes.length);
  return motivationalQuotes[randomIndex];
};

export const getTimeBasedGreeting = (): TimeGreeting => {
  const hour = new Date().getHours();

  if (hour >= 5 && hour < 12) {
    return {
      greeting: 'Let’s make today count',
      icon: React.createElement(WbSunnyIcon, { sx: { color: '#FFFFFF', fontSize: 28 } }),
      gradient: `linear-gradient(135deg,rgb(255, 238, 90),rgb(243, 124, 44))`,
      bgColor: '#FFF9C415'
    };
  } else if (hour >= 12 && hour < 17) {
    return {
      greeting: 'Keep the momentum going',
      icon: React.createElement(WbSunnyIcon, { sx: { color: '#FFFFFF', fontSize: 28 } }),
      gradient: `linear-gradient(135deg, #FFE082, #FFCA28)`,
      bgColor: '#FFF3E015'
    };
  } else if (hour >= 17 && hour < 20) {
    return {
      greeting: 'Every effort counts',
      icon: React.createElement(WbTwilightIcon, { sx: { color: '#FFFFFF', fontSize: 28 } }),
      gradient: `linear-gradient(135deg, #FFAB91, #FF7043)`,
      bgColor: '#FBE9E715'
    };
  } else if (hour >= 20 && hour < 24) {
    return {
      greeting: 'Stay curious, keep learning',
      icon: React.createElement(Brightness3Icon, { sx: { color: '#FFFFFF', fontSize: 28 } }),
      gradient: `linear-gradient(135deg, #B39DDB, #7E57C2)`,
      bgColor: '#EDE7F615'
    };
  } else {
    return {
      greeting: 'Discipline fuels progress',
      icon: React.createElement(NightlightIcon, { sx: { color: '#FFFFFF', fontSize: 28 } }),
      gradient: `linear-gradient(135deg, #90CAF9, #1565C0)`,
      bgColor: '#E3F2FD15'
    };
  }
};

export const formatFileSize = (bytes: string | number): string => {
  if (!bytes) return 'N/A';
  const numBytes = typeof bytes === 'string' ? parseInt(bytes) : bytes;
  if (isNaN(numBytes)) return 'N/A';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(numBytes) / Math.log(1024));
  return Math.round((numBytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
};

export const getContentTypeIcon = (contentType: string) => {
  const iconProps = { fontSize: 16 };
  switch (contentType.toLowerCase()) {
    case 'pdf':
      return React.createElement(PictureAsPdf, { sx: { ...iconProps, color: '#dc2626' } });
    case 'mp4':
    case 'video':
      return React.createElement(VideoFile, { sx: { ...iconProps, color: '#2563eb' } });
    case 'quiz':
      return React.createElement(Quiz, { sx: { ...iconProps, color: '#7c3aed' } });
    case 'qna':
      return React.createElement(Help, { sx: { ...iconProps, color: '#059669' } });
    case 'html':
      return React.createElement(Html, { sx: { ...iconProps, color: '#f97316' } });
    case 'json':
      return React.createElement(DataObject, { sx: { ...iconProps, color: '#8b5cf6' } });
    case 'xlsx':
    case 'xls':
    case 'excel':
      return React.createElement(TableChart, { sx: { ...iconProps, color: '#16a34a' } });
    default:
      return React.createElement(Description, { sx: { ...iconProps, color: '#6b7280' } });
  }
};

export const getContentTypeColor = (contentType: string): string => {
  switch (contentType.toLowerCase()) {
    case 'pdf':
      return '#dc2626';
    case 'mp4':
    case 'video':
      return '#2563eb';
    case 'quiz':
      return '#7c3aed';
    case 'qna':
      return '#059669';
    case 'html':
      return '#f97316';
    case 'json':
      return '#8b5cf6';
    default:
      return '#6b7280';
  }
};

export const COURSE_IMAGES = [
  'https://www.shutterstock.com/image-photo/open-reading-book-completely-blank-600nw-1723941556.jpg',
  'https://img.freepik.com/free-photo/open-book-blue-background-isolated-closeup_169016-25939.jpg',
  'https://img.freepik.com/free-photo/3d-stack-books_23-2151103673.jpg',
  'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
  'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
  'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
  'https://images.unsplash.com/photo-1501504905252-473c47e087f8',
  'https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
  'https://images.unsplash.com/photo-1553877522-43269d4ea984'
];

export const getContentImage = (contentName: string) => {
  const imageMap: { [key: string]: string } = {
    'The Data Detective': 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop&auto=format',
    'The Strategy Shaper': 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=400&h=250&fit=crop&auto=format',
    'The Digital Architect': 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400&h=250&fit=crop&auto=format',
    'Advance Your Skills in Deep Learning and Neural Networks':
      'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=250&fit=crop&auto=format',
    'Building ChatGPT Skills for Business Professionals':
      'https://images.unsplash.com/photo-1676299081847-824916de030a?w=400&h=250&fit=crop&auto=format',

    'The Economic Explorer': 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400&h=250&fit=crop&auto=format',
    'The Finance Forward': 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=250&fit=crop&auto=format',
    'The Language Luminary': 'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=400&h=250&fit=crop&auto=format',
    'The Market Maven': 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=250&fit=crop&auto=format',
    'The Mindful Learner': 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=250&fit=crop&auto=format',
    'The Digital Communicator': 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=250&fit=crop&auto=format',
    'The Tech Tinkerer': 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=400&h=250&fit=crop&auto=format',

    'The Accounting Analyst': 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=400&h=250&fit=crop&auto=format',
    'LinkedIn Learning Human Skills for Managers Professional Certificate':
      'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=400&h=250&fit=crop&auto=format',
    'Master Your Leadership Effectiveness Skills': 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=400&h=250&fit=crop&auto=format',
    'Develop Critical Thinking, Decision-Making, and Problem-Solving Skills':
      'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=250&fit=crop&auto=format',
    'Career Essentials in Cybersecurity by Microsoft and LinkedIn':
      'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=400&h=250&fit=crop&auto=format',
    'The Career Starter': 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=400&h=250&fit=crop&auto=format',
    'ORIENTATION': 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=400&h=250&fit=crop&auto=format',
    "Mastering Negotiations with a CEO's mindset: From Manager to Strategist":
      'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=250&fit=crop&auto=format',
    'Introduction to Excel for Business & Excel Functions':
      'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=400&h=250&fit=crop&auto=format',
    'Advanced Functions and Formulas': 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop&auto=format',
    'Masterclass on Cybersecurity': 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=400&h=250&fit=crop&auto=format',
    'Charts, Pivot and Statistical functions in excel':
      'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=250&fit=crop&auto=format',
    'Future-Proof: Thriving in an AI-Powered Workplace':
      'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=250&fit=crop&auto=format'
  };

  return imageMap[contentName] || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=250&fit=crop&auto=format';
};
export const SEMESTER_IMAGES = [
  'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=200&fit=crop',
  'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=200&fit=crop',
  'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=400&h=200&fit=crop',
  'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=400&h=200&fit=crop'
] as const;

export const getLearningStyleIcon = (learningName: string) => {
  const name = learningName.toLowerCase();

  if (name.includes('reading') || name.includes('text')) {
    return React.createElement(MenuBook, { sx: { fontSize: 16 } });
  } else if (name.includes('video') || name.includes('multimedia')) {
    return React.createElement(VideoLibrary, { sx: { fontSize: 16 } });
  } else if (name.includes('interactive') || name.includes('simulation')) {
    return React.createElement(Computer, { sx: { fontSize: 16 } });
  } else if (name.includes('assignment') || name.includes('task')) {
    return React.createElement(Assignment, { sx: { fontSize: 16 } });
  } else if (name.includes('quiz') || name.includes('test') || name.includes('assessment')) {
    return React.createElement(Quiz, { sx: { fontSize: 16 } });
  } else if (name.includes('practical') || name.includes('lab')) {
    return React.createElement(Science, { sx: { fontSize: 16 } });
  } else if (name.includes('lecture') || name.includes('presentation')) {
    return React.createElement(School, { sx: { fontSize: 16 } });
  } else {
    return React.createElement(Description, { sx: { fontSize: 16 } });
  }
};

export const getLearningStyleColor = (learningName: string) => {
  const name = learningName.toLowerCase();

  if (name.includes('reading') || name.includes('text')) {
    return '#2196f3';
  } else if (name.includes('video') || name.includes('multimedia')) {
    return '#f44336';
  } else if (name.includes('interactive') || name.includes('simulation')) {
    return '#4caf50';
  } else if (name.includes('assignment') || name.includes('task')) {
    return '#ff9800';
  } else if (name.includes('quiz') || name.includes('test') || name.includes('assessment')) {
    return '#9c27b0';
  } else if (name.includes('practical') || name.includes('lab')) {
    return '#607d8b';
  } else if (name.includes('lecture') || name.includes('presentation')) {
    return '#795548';
  } else {
    return '#6b7280';
  }
};

export const safeCapitalize = (text: any) => {
  try {
    if (!text || typeof text !== 'string') return '';
    return text.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  } catch {
    return String(text || '');
  }
};

export const getTouchpointFromPath = (path: string): string => {
  const touchpointMap: Record<string, string> = {
    '/': 'HOME_PAGE',
    '/case-crumbs': 'CASE_CRUMBS_PAGE',
    '/performance': 'PERFORMANCE_PAGE',
    '/help': 'HELP_PAGE',

    
  };

  if (path.startsWith('/case-crumbs-player/')) {
    return 'CASE_CRUMBS_PLAYER_PAGE';
  }

  return touchpointMap[path] || 'STUDENT_DASHBOARD';
};